import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * Supports two modes:
 *  1) Legacy PDP mode: finds product form + `.buy-buttons-block` anchor (existing behavior).
 *  2) Trigger mode (for Featured Product / custom layouts): observes `[data-sticky-atc-trigger]`
 *     matching `data-section-id` from this element.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up IntersectionObservers:
   * - One observing the "buy buttons anchor" (legacy buy-buttons-block OR explicit trigger)
   * - One observing the footer to hide sticky bar at the very bottom (if footer exists)
   */
  #setupIntersectionObserver() {
    const { buyAnchor, footer } = this.#getObserverTargets();
    if (!buyAnchor) return;

    // Observer for buy buttons / trigger visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        // Only show if we have scrolled past it (anchor is above viewport)
        const rect = entry.target.getBoundingClientRect();
        if ((rect.bottom < 0 || rect.top < 0) && !this.#hiddenByBottom) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyAnchor);

    // The sticky bar stays visible over the footer, except while the footnotes
    // at the very bottom of the footer are in view: there it would cover them.
    void footer;
    const footnotes = document.querySelector('[data-evr-footnotes]');
    if (footnotes) {
      this.#mainBottomObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          if (this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          }
        } else if (this.#hiddenByBottom) {
          this.#hiddenByBottom = false;
          const anchorRect = buyAnchor.getBoundingClientRect();
          if (anchorRect.bottom < 0) this.#showStickyBar();
        }
      });
      this.#mainBottomObserver.observe(footnotes);
    }

    // Keep puppet click behavior: find the real add-to-cart button in the real form if possible
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Resolve observer targets with strict backward compatibility:
   * 1) If an explicit trigger exists for this section instance, use it.
   * 2) Otherwise, use legacy PDP anchor: productForm.closest('.buy-buttons-block').
   * Footer is optional; missing footer must not disable sticky ATC.
   */
  #getObserverTargets() {
    const sectionId = this.dataset.sectionId || this.closest('.shopify-section')?.id?.replace('shopify-section-', '');

    // 1) Trigger mode (Featured Product / custom layouts)
    /** @type {Element | null} */
    let buyAnchor = null;

    if (sectionId) {
      buyAnchor = document.querySelector(
        `[data-sticky-atc-trigger][data-section-id="${sectionId}"]`
      );
    }

    // 2) Legacy PDP mode
    if (!buyAnchor) {
      const productForm = this.#getProductForm();
      buyAnchor = productForm?.closest('.buy-buttons-block') ?? null;
    }

    // Footer fallback
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    return { buyAnchor, footer };
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Insurance-check mode: instead of adding to cart, scroll to the insurance
   * check block, try to open the insurer dropdown, and highlight it as a
   * fallback. Logs each step under [sticky-atc] for debugging.
   */
  handleScrollToInsuranceClick = () => {
    const log = (...args) => console.log('[sticky-atc]', ...args);

    const selector = this.refs.addToCartButton?.dataset.insuranceTarget || '[data-evr-insurance-check]';
    log('click → target selector:', selector);

    const target = document.querySelector(selector);
    if (!target) {
      console.warn('[sticky-atc] target not found for selector:', selector);
      return;
    }
    log('target found:', target);

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const selectWrap = /** @type {HTMLElement | null} */ (target.querySelector('[data-evr-selectwrap]'));
    const loading = /** @type {HTMLElement | null} */ (target.querySelector('[data-evr-loading]'));
    const select = /** @type {HTMLSelectElement | null} */ (target.querySelector('[data-evr-select]'));

    log('elements:', {
      hasSelectWrap: !!selectWrap,
      selectWrapHidden: selectWrap?.hidden,
      hasLoading: !!loading,
      loadingHidden: loading?.hidden,
      hasSelect: !!select,
      selectDisabled: select?.disabled,
      optionCount: select?.options.length,
      showPickerSupported: typeof select?.showPicker === 'function',
    });

    // Report whether the browser still considers this a user gesture, which
    // showPicker() requires. (Not all browsers expose navigator.userActivation.)
    if (navigator.userActivation) {
      log('userActivation:', {
        isActive: navigator.userActivation.isActive,
        hasBeenActive: navigator.userActivation.hasBeenActive,
      });
    } else {
      log('navigator.userActivation: not supported');
    }

    // Highlight as a visible fallback regardless of picker support.
    const highlightEl = selectWrap && !selectWrap.hidden ? selectWrap : loading && !loading.hidden ? loading : selectWrap;
    if (highlightEl) {
      this.#flashAttention(highlightEl);
      log('highlight applied to:', highlightEl);
    }

    const openPicker = () => {
      if (!select || select.disabled) {
        log('openPicker: select missing or disabled — skipping');
        return;
      }
      if (typeof select.showPicker !== 'function') {
        log('openPicker: showPicker() not supported');
        return;
      }
      select.focus({ preventScroll: true });
      try {
        select.showPicker();
        log('openPicker: showPicker() succeeded', {
          activationStillActive: navigator.userActivation?.isActive,
        });
      } catch (error) {
        console.warn('[sticky-atc] openPicker: showPicker() threw:', error);
      }
    };

    // Opening the picker mid-scroll fails silently — any scroll movement
    // dismisses a native <select> popup. So wait until the scroll settles.
    const rect = target.getBoundingClientRect();
    const alreadyInView = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (alreadyInView) {
      log('target already in view → opening immediately');
      openPicker();
      return;
    }

    log('scrolling → will open picker once scroll settles');
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    let settled = false;
    const onSettled = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('scrollend', onSettled);
      clearTimeout(fallbackTimer);
      log('scroll settled → opening picker now');
      openPicker();
    };

    // `scrollend` is the precise signal; the timeout is a fallback for browsers
    // that don't fire it (older Safari) or when no scroll was needed.
    window.addEventListener('scrollend', onSettled, { once: true });
    const fallbackTimer = setTimeout(onSettled, 700);
  };

  /**
   * Restarts the attention pulse on an element.
   * @param {HTMLElement} el
   */
  #flashAttention(el) {
    el.classList.remove('evr-ddl-attention');
    // Force reflow so the animation restarts if the user clicks again.
    void el.offsetWidth;
    el.classList.add('evr-ddl-attention');
    el.addEventListener(
      'animationend',
      () => el.classList.remove('evr-ddl-attention'),
      { once: true }
    );
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element (legacy PDP mode)
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    // Keep existing selector first, add safe fallbacks (no behavior change on PDP)
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"], ` +
        `#shopify-section-${sectionId} product-form[data-product-id="${productId}"], ` +
        `#shopify-section-${sectionId} [data-product-form][data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}